import { faQuestionCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import TagSearch from 'components/tags/TagSearch';
import useApi from 'helpers/api';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';

const ContactCustomerRegistrationModal = ({
  showModal,
  handleClose,
  handleSubmit,
  customer
}) => {
  const api = useApi();
  const [customers, setCustomers] = useState([]);
  const [filters] = useState({ size: 100, includeInactives: false });

  const initialState = {
    name: '',
    email: '',
    phone: '',
    landline: '',
    inactive: false,
    isPortalUser: false,
    isAdmin: false,
    customer: { id: customer?.id },
    tags: []
  };
  const [body, setBody] = useState(initialState);
  const [validated, setValidated] = useState(false);
  const form = useRef(null);

  useEffect(() => {
    if (showModal) {
      clearFields();
      setValidated(false);
    }
  }, [showModal]);

  useEffect(() => {
    getCustomers();
  }, [filters.name]);

  const getCustomers = async () => {
    if (filters.name != '') {
      const queryParams = new URLSearchParams(filters).toString();
      let response = await api.customers.list(queryParams);
      setCustomers(response.content);
    }
  };

  const onSubmit = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      setValidated(false);
      handleSubmit({
        name: body.name,
        email: body.email === '' ? null : body.email,
        phone: body.phone === '' ? null : body.phone,
        landline: body.landline === '' ? null : body.landline,
        customer: { id: customer ? customer.id : body.customer.id },
        inactive: false,
        isPortalUser: body.isPortalUser,
        isAdmin: body.isAdmin,
        tags: body.tags
      });
      clearFields();
    }
  };

  const clearFields = () => {
    setBody(initialState);
  };

  const handleAddTag = tag => {
    if (tag.trim() !== '' && !body.tags.includes(tag.trim())) {
      setBody(prevState => ({
        ...prevState,
        tags: [...prevState.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = tag => {
    setBody(prevState => ({
      ...prevState,
      tags: prevState.tags.filter(t => t !== tag)
    }));
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Novo contato</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Form.Group className="mb-3" controlId="contact.name">
            <Form.Label>
              Nome <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="Nome"
              autoFocus
              value={body.name}
              onChange={e =>
                setBody(prevState => ({ ...prevState, name: e.target.value }))
              }
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um nome.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="contact.email">
            <Form.Label>
              E-mail{' '}
              {body.phone === '' && body.landline === '' && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <Form.Control
              required={body.phone === '' && body.landline === ''}
              type="email"
              placeholder="E-mail"
              value={body.email}
              onChange={e =>
                setBody(prevState => ({ ...prevState, email: e.target.value }))
              }
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um e-mail.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="agent.phone">
            <Form.Label>
              Telefone{' '}
              {body.email === '' && body.phone === '' && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <MaskedInput
              mask={[
                '(',
                /[1-9]/,
                /\d/,
                ')',
                ' ',
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                /\d/,
                /\d/
              ]}
              placeholder="(00) 0000-0000"
              required={body.email === '' && body.phone === ''}
              guide={false}
              value={body.landline}
              onChange={e =>
                setBody(prevBody => ({ ...prevBody, landline: e.target.value }))
              }
              render={(ref, props) => <Form.Control ref={ref} {...props} />}
            />

            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um número de telefone.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="agent.phone">
            <Form.Label>
              Celular{' '}
              {body.email === '' && body.landline === '' && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>
            <MaskedInput
              mask={[
                '(',
                /[1-9]/,
                /\d/,
                ')',
                ' ',
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                /\d/,
                '-',
                /\d/,
                /\d/,
                /\d/,
                /\d/
              ]}
              placeholder="(00) 00000-0000"
              required={body.email === '' && body.landline === ''}
              guide={false}
              value={body.phone}
              onChange={e =>
                setBody(prevBody => ({ ...prevBody, phone: e.target.value }))
              }
              render={(ref, props) => <Form.Control ref={ref} {...props} />}
            />

            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um número de celular.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="contact.tags">
            <Form.Label>Tags</Form.Label>
            <TagSearch onAddTag={handleAddTag} />
            <div className="mt-1">
              {body.tags.map((tag, index) => (
                <Badge
                  key={index}
                  bg="light"
                  text="dark"
                  className="me-1 p-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRemoveTag(tag)}
                >
                  {tag} &times;
                </Badge>
              ))}
            </div>
          </Form.Group>

          <Form.Group
            as={Flex}
            alignItems="center"
            justifyContent="between"
            className="mb-2"
          >
            <Form.Label>Ativo</Form.Label>
            <Form.Check
              type="switch"
              value={body.inactive}
              checked={!body.inactive}
              disabled
              onChange={e => setBody({ ...body, inactive: !body.inactive })}
            />
          </Form.Group>

          <Form.Group
            as={Flex}
            alignItems="center"
            justifyContent="between"
            className="mb-1"
          >
            <Form.Label>Tem acesso ao portal do cliente?</Form.Label>
            <Form.Check
              type="switch"
              value={body.isPortalUser}
              checked={body.isPortalUser}
              onChange={e =>
                setBody({ ...body, isPortalUser: !body.isPortalUser })
              }
            />
          </Form.Group>

          {body.isPortalUser && (
            <Form.Group
              as={Flex}
              alignItems="center"
              justifyContent="between"
              className="ms-3"
            >
              <Form.Label>
                Administrador
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      Usuários administradores podem visualizar todos os tickets
                      dos clientes associados a eles.
                    </Tooltip>
                  }
                >
                  <span className="ms-1">
                    <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Check
                type="switch"
                value={body.isAdmin}
                checked={body.isAdmin}
                onChange={e => setBody({ ...body, isAdmin: !body.isAdmin })}
              />
            </Form.Group>
          )}

          <Form.Group controlId="contact.customer">
            <Form.Label>
              Cliente principal <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              defaultValue={customer?.id}
              value={body.customer.id}
              disabled
            >
              {customers.map((item, i) => (
                <option value={item.id} key={i}>
                  {item.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira um cliente.
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactCustomerRegistrationModal;
